/* eslint-disable @typescript-eslint/no-explicit-any */

import { useEffect } from 'react';
import {
  Flex,
  FormControl,
  FormLabel,
  Switch,
  Grid,
  GridItem,
  HStack,
  Input,
  Text,
  VStack,
  Box,
  Image,
  Checkbox,
  CheckboxGroup,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  Controller,
  Control,
  FieldErrors,
  UseFormWatch,
  UseFormSetValue,
  UseFormClearErrors,
} from 'react-hook-form';
import Asterisk from '@/components/common/Asterisk';
import { FormData, StyleField, AddOnFields } from '@/services/addProduct/types';
import {
  useGetAddOnFields,
  useGetStyleFields,
  useGetCategories,
  useGetSizes,
} from '@/services/addProduct';
import { useStore } from '@/store/store';
import { IProductEditResponse } from '@/services/manageProduct/types';

interface CustomizeSectionProps {
  control: Control<FormData>;
  errors: FieldErrors<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  clearErrors: UseFormClearErrors<FormData>;
  trigger: any;
  product?: IProductEditResponse;
  id?: string;
}

const CustomizeSection = ({
  control,
  errors,
  watch,
  setValue,
  clearErrors,
}: CustomizeSectionProps) => {
  const selectedCategoryId = watch('categorySelection.category');
  const variants = watch('variants') || [];
  const { user } = useStore();
  const { data: styleFields = [] } = useGetStyleFields();
  const { data: addOnFields = [] } = useGetAddOnFields();
  const { data: categories = [] } = useGetCategories();
  const { data: sizes = [] } = useGetSizes();

  const isCustomizable = watch('is_customizable');

  const selectedCategory = categories.find((cat: any) => cat.id.toString() === selectedCategoryId);
  const selectedCategoryName = selectedCategory?.name || '';

  const customizationType = selectedCategoryName.toLowerCase().includes('saree')
    ? 'Blouse'
    : selectedCategoryName.toLowerCase().includes('kurta')
      ? 'Kurta Style'
      : 'Style';

  const filteredAddOnFields = addOnFields.filter(
    (addOn: AddOnFields) => addOn.category.name === selectedCategoryName && addOn.is_active,
  );

  const activeStyleFields = styleFields.filter(
    (field: StyleField) => field.category.name === selectedCategoryName && field.is_active,
  );
  const vendorCurrency = user?.currency || 'NPR'; // ← Always up-to-date
  const currencySymbol = vendorCurrency === 'NPR' ? 'Rs.' : '$';
  const hasAddOnOptions = filteredAddOnFields.length > 0;
  const hasStyleOptions = activeStyleFields.length > 0;

  // Detect if "Custom Size Only" is selected anywhere
  const hasCustomSizeOnly = variants.some((variant: any) =>
    variant.sizes?.some((size: any) => {
      const selectedSize = sizes.find((s: any) => String(s.id) === String(size.size));
      return selectedSize?.value === 'Custom Size Only';
    }),
  );

  // "Custom Size Only" is made-to-measurement → customization is mandatory.
  // Force the toggle on so it can't be submitted as a non-customizable product.
  useEffect(() => {
    if (hasCustomSizeOnly && !isCustomizable) {
      setValue('is_customizable', true);
    }
  }, [hasCustomSizeOnly, isCustomizable, setValue]);

  // MAIN LOGIC: When to show the ENTIRE customization section
  const shouldShowCustomizationSection = hasStyleOptions || (!hasStyleOptions && hasCustomSizeOnly);

  // Always show add-ons if they exist
  const shouldShowAddOnsSection = hasAddOnOptions;

  // If no category selected → hide everything
  if (!selectedCategoryId) return null;

  // If neither add-ons nor customization should appear → hide entire box
  if (!shouldShowAddOnsSection && !shouldShowCustomizationSection) {
    return null;
  }

  const handleCustomizeToggle = (isChecked: boolean) => {
    // "Custom Size Only" must stay customizable — block turning it off.
    if (!isChecked && hasCustomSizeOnly) {
      setValue('is_customizable', true);
      return;
    }
    if (!isChecked) {
      setValue('measurement_fields', []);
      setValue('customized_price', '0');
      setValue('customized_weight', '0.00');
      clearErrors(['measurement_fields', 'customized_price', 'customized_weight']);
    }
  };

  return (
    <>
      {shouldShowCustomizationSection && (
        <Flex
          gap={5}
          direction="column"
          bg="background.secondary"
          p={4}
          borderWidth="1px"
          borderRadius="md"
        >
          <Text fontWeight="semibold" fontSize="lg">
            Customize Size
          </Text>
          <Grid gap={8}>
            <GridItem>
              {/* The toggle is a real choice only for style-based categories.
                  For "Custom Size Only" it's forced on, so we hide it and just
                  show a note + the price below. */}
              {!hasCustomSizeOnly && (
                <FormControl display="flex" alignItems="center">
                  <Controller
                    name="is_customizable"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        id="customize-style"
                        isChecked={field.value}
                        onChange={(e) => {
                          field.onChange(e.target.checked);
                          handleCustomizeToggle(e.target.checked);
                        }}
                        colorScheme="brand.primary"
                        mr={2}
                      />
                    )}
                  />
                  <FormLabel htmlFor="customize-style" mb="0">
                    Enable the option to customize and select your{' '}
                    {customizationType.toLowerCase()} for a perfect match.
                  </FormLabel>
                </FormControl>
              )}
              {hasCustomSizeOnly && (
                <Text fontSize="sm" color="text.secondary">
                  This item is made to your measurements — set the customization price below.
                </Text>
              )}

              {isCustomizable && (
                <VStack spacing={4} mt={4} align="stretch">
                  <HStack
                    spacing={6}
                    direction={{ base: 'column', md: 'row' }}
                    align="start"
                    w="full"
                  >
                    <FormControl
                      isInvalid={!!errors.customized_price}
                      width={hasStyleOptions ? 'auto' : { base: '100%', md: '50%' }}
                      flex={hasStyleOptions ? '1' : undefined}
                    >
                      <FormLabel>
                        Customize {customizationType} Price <Asterisk />
                      </FormLabel>
                      <Box position="relative">
                        <Text
                          position="absolute"
                          left="10px"
                          top="50%"
                          transform="translateY(-50%)"
                          color="text.primary"
                          pointerEvents="none"
                          zIndex={10}
                        >
                          {currencySymbol}
                        </Text>
                        <Controller
                          name="customized_price"
                          control={control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              placeholder="0.00"
                              bg="white"
                              pl="30px"
                              value={field.value || ''}
                              onChange={(e) => field.onChange(e.target.value)}
                              onFocus={() =>
                                (field.value === '0' || field.value === '0.00') &&
                                field.onChange('')
                              }
                            />
                          )}
                        />
                      </Box>
                      <FormErrorMessage>{errors.customized_price?.message}</FormErrorMessage>
                    </FormControl>

                    {hasStyleOptions && (
                      <FormControl isInvalid={!!errors.customized_weight} flex="1">
                        <FormLabel>
                          {customizationType} Weight (kg)
                          {(watch('measurement_fields')?.length || 0) > 0 && <Asterisk />}
                        </FormLabel>
                        <Controller
                          name="customized_weight"
                          control={control}
                          render={({ field }) => (
                            <Input
                              type="number"
                              placeholder="0.00"
                              bg="white"
                              value={field.value || ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v.includes('.') && v.split('.')[1].length > 2) return;
                                field.onChange(v);
                              }}
                              onFocus={() => field.value === '0.00' && field.onChange('')}
                              step="0.01"
                            />
                          )}
                        />
                        <FormErrorMessage>{errors.customized_weight?.message}</FormErrorMessage>
                      </FormControl>
                    )}
                  </HStack>

                  {hasStyleOptions && (
                    <>
                      <Text color="text.secondary">Select {customizationType} Design Options</Text>
                      <FormControl isInvalid={!!errors.measurement_fields}>
                        <Controller
                          name="measurement_fields"
                          control={control}
                          render={({ field }) => (
                            <CheckboxGroup
                              value={field.value?.map(String) || []}
                              onChange={(vals) => {
                                const nums = vals.map(Number).filter((n) => !isNaN(n));
                                field.onChange(nums);
                                if (nums.length > 0) clearErrors('measurement_fields');
                              }}
                            >
                              <Grid
                                templateColumns={{
                                  base: '1fr',
                                  md: 'repeat(2, 1fr)',
                                  lg: 'repeat(3, 1fr)',
                                }}
                                gap={4}
                              >
                                {activeStyleFields.map((option: StyleField) => (
                                  <Checkbox
                                    key={option.id}
                                    value={String(option.id)}
                                    colorScheme="brand.primary"
                                  >
                                    <HStack spacing={2}>
                                      {option.image_small && (
                                        <Image
                                          src={option.image_small}
                                          boxSize="20px"
                                          borderRadius="md"
                                        />
                                      )}
                                      <Text>{option.name}</Text>
                                    </HStack>
                                  </Checkbox>
                                ))}
                              </Grid>
                            </CheckboxGroup>
                          )}
                        />
                        <FormErrorMessage>{errors.measurement_fields?.message}</FormErrorMessage>
                      </FormControl>
                    </>
                  )}
                </VStack>
              )}
            </GridItem>
          </Grid>
        </Flex>
      )}
    </>
  );
};

export default CustomizeSection;
